'use client';

import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useLoginState } from '@packages/utilities';
import type { GTMEventPageMetaData } from '@packages/tracking/src/hooks/useTracking';
import { useTracking } from '@packages/tracking/src/hooks/useTracking';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot';
import { GlycerinLoginStatus, type GTMEventGlycerinPageview } from '@packages/tracking/src/types';
import { mapPageTypeAndTemplate } from '@packages/tracking/src/utils/mapPageTypeAndTemplate';
import { useI18n } from '@packages/shared';
import { useCustomerInformation } from '../hooks';

export type PageMetaDataProps = Omit<
  GTMEventPageMetaData,
  'event' | 'eventAction' | 'eventCategory' | 'eventLabel' | 'eventValue' | 'page'
> & {
  /** page meta data information */
  page: {
    /** the page type (e.g. 'serp', 'detailview') */
    type: string;
    /** optional template information */
    template?: string;
  };
  /** optional url pathname for useEffect deps array */
  pathname?: string;
};

/**
 * This component dispatches the following GTM events:
 * - dataLayerReady
 * - pageMetaData
 * */
export const PageMetaData: FC<PageMetaDataProps> = ({
  page,
  categoryPath,
  pathname,
  query,
  searchId,
}) => {
  const dispatchGTMEvent = useTracking();
  const isBot = useIsBot();
  const { isLoading, customerInformation } = useCustomerInformation();
  const { loggedIn, softLoggedIn } = useLoginState();
  const { language, locale } = useI18n();
  const [loginStatus, setLoginStatus] = useState<GlycerinLoginStatus>();

  const { type, template } = page;

  useEffect(() => {
    if (softLoggedIn) {
      setLoginStatus(GlycerinLoginStatus.SOFTLOGGEDIN);
      return;
    }
    if (loggedIn) {
      setLoginStatus(GlycerinLoginStatus.LOGGEDIN);
      return;
    }
    setLoginStatus(GlycerinLoginStatus.NOTLOGGEDIN);
  }, [loggedIn, softLoggedIn]);

  useEffect(() => {
    if (!isLoading && loginStatus && customerInformation) {
      dispatchGTMEvent({
        event: 'dataLayerReady',
        Sprache: locale,
        LoginStatus: loginStatus,
        ...customerInformation,
        visitor: {
          ...customerInformation?.visitor,
          login: loggedIn,
        },
      });
    }
    // Fix for CORE-556: searchId has been added to trigger dataLayerReady on MBA/SERP changes.
    // TODO: rework events within tracking project
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, customerInformation?.visitor.login, locale, loggedIn, searchId, loginStatus]);

  /** Fire glycerin pageview event */
  useEffect(() => {
    if (!isLoading && loginStatus) {
      const event: GTMEventGlycerinPageview = {
        event: 'Pageview',
        PageviewData: {
          loginStatus,
          pageTypes: mapPageTypeAndTemplate(type, template),
          language,
        },
      };
      dispatchGTMEvent(event);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, pathname, template, query, isBot, language, loginStatus]);

  useEffect(() => {
    const event: GTMEventPageMetaData = {
      event: 'pageMetaData',
      page: {
        type,
        template,
        bot: isBot,
      },
      categoryPath,
      query,
      searchId,
      pageViewLanguage: language,
    };
    if (typeof window !== 'undefined') {
      (window as any).pagetype = type;
    }
    dispatchGTMEvent(event);
    // remove categoryPath from array, it is not a primitive value and is kinda unpredictable
    // remove searchId from array, it changes for every settings change and is only relevant for MBA and SERP
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, pathname, template, query, isBot]);

  return <div data-testid="page-meta-data" />;
};
